import React, { useState, useEffect } from 'react'
import { Card, Table, Container, Button, Badge, InputGroup, Form, Modal, Row, Col, OverlayTrigger, Tooltip, Alert, Spinner } from 'react-bootstrap'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'
import { v4 as uuidv4 } from 'uuid' // npm install uuid

// ─── Couleurs par sport ───────────────────────────────────────────────────────
const SPORT_COLORS = ['#4f46e5', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#ca8a04']
const getSportColor = (i) => SPORT_COLORS[i % SPORT_COLORS.length]
const DAYS_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']

// Liste de sports disponibles (à adapter selon votre contexte)
const SPORTS_DISPONIBLES = ['Football', 'Basketball', 'Tennis', 'Natation', 'Athlétisme', 'Boxe', 'Judo', 'Yoga', 'Fitness', 'Handball', 'Volleyball', 'Rugby']

const EMPTY_FORM = { first_name: '', last_name: '', sports: [], rgpd_consent: false }

function Coaches() {
  const [coaches, setCoaches] = useState([])
  const [filteredCoaches, setFilteredCoaches] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  // QR Modal
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedCoach, setSelectedCoach] = useState(null)

  // Stats Modal
  const [showStatsModal, setShowStatsModal] = useState(false)
  const [statsCoach, setStatsCoach] = useState(null)
  const [statsData, setStatsData] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [presencesList, setPresencesList] = useState([])

  // ─── Modal Modification Présence ─────────────────────────────────────────────
  const [showEditPresenceModal, setShowEditPresenceModal] = useState(false)
  const [editingPresence, setEditingPresence] = useState(null)
  const [editPresenceDate, setEditPresenceDate] = useState('')
  const [editPresenceLoading, setEditPresenceLoading] = useState(false)
  const [editPresenceError, setEditPresenceError] = useState(null)

  // ─── Modal Suppression Présence ──────────────────────────────────────────────
  const [showDeletePresenceModal, setShowDeletePresenceModal] = useState(false)
  const [presenceToDelete, setPresenceToDelete] = useState(null)
  const [deletePresenceLoading, setDeletePresenceLoading] = useState(false)

  // ─── Modal Ajout / Modification ──────────────────────────────────────────────
  const [showFormModal, setShowFormModal] = useState(false)
  const [formMode, setFormMode] = useState('add') // 'add' | 'edit'
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [editingCoach, setEditingCoach] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState(null)
  const [formSuccess, setFormSuccess] = useState(null)
  const [sportInput, setSportInput] = useState('')

  // ─── Modal Suppression ───────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [coachToDelete, setCoachToDelete] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => { fetchCoaches() }, [])

  useEffect(() => {
    if (!searchTerm) { setFilteredCoaches(coaches); return }
    const term = searchTerm.toLowerCase()
    setFilteredCoaches(coaches.filter(c =>
      (c.first_name || '').toLowerCase().includes(term) ||
      (c.last_name || '').toLowerCase().includes(term) ||
      (c.sports || []).some(s => s.toLowerCase().includes(term))
    ))
  }, [coaches, searchTerm])

  const fetchCoaches = async () => {
    try {
      const { data, error } = await supabase
        .from('coaches').select('*').order('created_at', { ascending: false })
      if (error) throw error
      setCoaches(data || [])
    } catch (error) {
      console.error('Error fetching coaches:', error)
    } finally {
      setLoading(false)
    }
  }

  // ─── AJOUT d'un coach ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setFormMode('add')
    setFormData(EMPTY_FORM)
    setSportInput('')
    setFormError(null)
    setFormSuccess(null)
    setEditingCoach(null)
    setShowFormModal(true)
  }

  // ─── MODIFICATION d'un coach ──────────────────────────────────────────────
  const openEditModal = (coach) => {
    setFormMode('edit')
    setFormData({
      first_name: coach.first_name || '',
      last_name: coach.last_name || '',
      sports: coach.sports || [],
      rgpd_consent: coach.rgpd_consent || false,
    })
    setSportInput('')
    setFormError(null)
    setFormSuccess(null)
    setEditingCoach(coach)
    setShowFormModal(true)
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const toggleSport = (sport) => {
    setFormData(prev => ({
      ...prev,
      sports: prev.sports.includes(sport)
        ? prev.sports.filter(s => s !== sport)
        : [...prev.sports, sport]
    }))
  }

  const addCustomSport = () => {
    const trimmed = sportInput.trim()
    if (trimmed && !formData.sports.includes(trimmed)) {
      setFormData(prev => ({ ...prev, sports: [...prev.sports, trimmed] }))
    }
    setSportInput('')
  }

  const removeSport = (sport) => {
    setFormData(prev => ({ ...prev, sports: prev.sports.filter(s => s !== sport) }))
  }

  const handleFormSubmit = async () => {
    setFormError(null)
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setFormError('Le prénom et le nom sont obligatoires.')
      return
    }
    setFormLoading(true)
    try {
      if (formMode === 'add') {
        const newCoach = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          sports: formData.sports,
          rgpd_consent: formData.rgpd_consent,
          token: uuidv4(),
        }
        const { error } = await supabase.from('coaches').insert([newCoach])
        if (error) throw error
        setFormSuccess('Coach ajouté avec succès !')
      } else {
        const updates = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          sports: formData.sports,
          rgpd_consent: formData.rgpd_consent,
        }
        const { error } = await supabase.from('coaches').update(updates).eq('id', editingCoach.id)
        if (error) throw error
        setFormSuccess('Coach modifié avec succès !')
      }
      await fetchCoaches()
      setTimeout(() => setShowFormModal(false), 1000)
    } catch (error) {
      console.error('Erreur form coach:', error)
      setFormError('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setFormLoading(false)
    }
  }

  // ─── SUPPRESSION d'un coach ───────────────────────────────────────────────
  const openDeleteModal = (coach) => {
    setCoachToDelete(coach)
    setShowDeleteModal(true)
  }

  const handleDeleteCoach = async () => {
    if (!coachToDelete) return
    setDeleteLoading(true)
    try {
      const { error } = await supabase.from('coaches').delete().eq('id', coachToDelete.id)
      if (error) throw error
      setShowDeleteModal(false)
      setCoachToDelete(null)
      await fetchCoaches()
    } catch (error) {
      console.error('Erreur suppression coach:', error)
    } finally {
      setDeleteLoading(false)
    }
  }

  // ─── Stats ────────────────────────────────────────────────────────────────
  const fetchCoachStats = async (coach) => {
    setStatsLoading(true)
    setStatsData(null)
    try {
      const { data, error } = await supabase
        .from('presences')
        .select(`id, timestamp, mode, sessions ( id, date, course_types ( id, name, day_of_week ) )`)
        .eq('coach_id', coach.id)
        .order('timestamp', { ascending: true })
      if (error) throw error
      const presences = data || []
      const totalSessions = presences.length
      const sportMap = {}
      presences.forEach(p => {
        const sport = p.sessions?.course_types?.name || 'Non défini'
        sportMap[sport] = (sportMap[sport] || 0) + 1
      })
      const sportStats = Object.entries(sportMap)
        .map(([sport, count]) => ({ sport, count, percent: totalSessions > 0 ? Math.round((count / totalSessions) * 100) : 0 }))
        .sort((a, b) => b.count - a.count)
      const dayMap = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
      presences.forEach(p => {
        const dateStr = p.sessions?.date || p.timestamp
        if (dateStr) dayMap[new Date(dateStr).getDay()]++
      })
      const dayStats = Object.entries(dayMap)
        .map(([day, count]) => ({ day: DAYS_FR[parseInt(day)], count, percent: totalSessions > 0 ? Math.round((count / totalSessions) * 100) : 0 }))
        .filter(d => d.count > 0)
        .sort((a, b) => b.count - a.count)
      const firstPresence = presences.length > 0 ? new Date(presences[0].timestamp).toLocaleDateString('fr-FR') : null
      const lastPresence = presences.length > 0 ? new Date(presences[presences.length - 1].timestamp).toLocaleDateString('fr-FR') : null
      // Stocker la liste brute triée du plus récent au plus ancien
      const sortedPresences = [...presences].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      setPresencesList(sortedPresences)
      setStatsData({ totalSessions, sportStats, dayStats, firstPresence, lastPresence })
    } catch (error) {
      setStatsData({ error: true })
    } finally {
      setStatsLoading(false)
    }
  }

  const openStatsModal = (coach) => { setStatsCoach(coach); setShowStatsModal(true); fetchCoachStats(coach) }

  // ─── Modifier une présence ────────────────────────────────────────────────
  const openEditPresenceModal = (presence) => {
    setEditingPresence(presence)
    // Pré-remplir avec la date de la session ou le timestamp
    const dateStr = presence.sessions?.date || presence.timestamp
    const d = new Date(dateStr)
    // Format YYYY-MM-DDTHH:MM pour input datetime-local
    const pad = n => String(n).padStart(2, '0')
    const formatted = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    setEditPresenceDate(formatted)
    setEditPresenceError(null)
    setShowEditPresenceModal(true)
  }

  const handleEditPresenceSubmit = async () => {
    if (!editPresenceDate) { setEditPresenceError('Veuillez saisir une date.'); return }
    setEditPresenceLoading(true)
    try {
      const { error } = await supabase
        .from('presences')
        .update({ timestamp: new Date(editPresenceDate).toISOString() })
        .eq('id', editingPresence.id)
      if (error) throw error
      setShowEditPresenceModal(false)
      await fetchCoachStats(statsCoach)
    } catch (err) {
      setEditPresenceError('Erreur lors de la modification.')
    } finally {
      setEditPresenceLoading(false)
    }
  }

  // ─── Supprimer une présence ───────────────────────────────────────────────
  const openDeletePresenceModal = (presence) => {
    setPresenceToDelete(presence)
    setShowDeletePresenceModal(true)
  }

  const handleDeletePresence = async () => {
    if (!presenceToDelete) return
    setDeletePresenceLoading(true)
    try {
      const { error } = await supabase.from('presences').delete().eq('id', presenceToDelete.id)
      if (error) throw error
      setShowDeletePresenceModal(false)
      setPresenceToDelete(null)
      await fetchCoachStats(statsCoach)
    } catch (err) {
      console.error('Erreur suppression présence:', err)
    } finally {
      setDeletePresenceLoading(false)
    }
  }

  const exportCoachesCSV = () => {
    const csvContent = [
      ['Prénom', 'Nom', 'Sports enseignés', 'Date inscription'],
      ...filteredCoaches.map(c => [c.first_name, c.last_name, (c.sports || []).join(' | '), new Date(c.created_at).toLocaleDateString('fr-FR')])
    ].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `coachs_street_sport_${new Date().toLocaleDateString('fr-FR')}.csv`)
    document.body.appendChild(link); link.click()
    document.body.removeChild(link); URL.revokeObjectURL(url)
  }

  const openQRModal = (coach) => { setSelectedCoach(coach); setShowQRModal(true) }

  const handlePrintQRCode = () => {
    const printWindow = window.open('', '_blank')
    printWindow.document.write(`<!DOCTYPE html><html><head><title>QR Code - Coach ${selectedCoach.first_name} ${selectedCoach.last_name}</title><style>body{font-family:Arial,sans-serif;text-align:center;padding:20px;}@media print{body{margin:0;padding:10px;}}</style></head><body><h2>Street Sport Luxembourg</h2><h3>Coach : ${selectedCoach.first_name} ${selectedCoach.last_name}</h3><p>Sports : ${(selectedCoach.sports || []).join(', ')}</p><svg width="300" height="300" viewBox="0 0 300 300">${document.getElementById('coach-qr-code-svg')?.innerHTML}</svg><p><small>Présentez ce QR code pour signer la feuille de présence</small></p></body></html>`)
    printWindow.document.close(); printWindow.print()
  }

  const downloadQRCodeAsPNG = () => {
    const svg = document.getElementById('coach-qr-code-svg')
    if (!svg) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.onload = () => {
      canvas.width = img.width; canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      const link = document.createElement('a')
      link.download = `qrcode_coach_${selectedCoach.first_name}_${selectedCoach.last_name}.png`
      link.href = canvas.toDataURL('image/png'); link.click()
    }
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  if (loading) return (
    <ProtectedRoute><Layout user={{}}>
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Chargement...</span></div>
        <p className="mt-3">Chargement des coachs...</p>
      </Container>
    </Layout></ProtectedRoute>
  )

  return (
    <ProtectedRoute>
      <Layout user={{}}>
        <Container fluid="lg">

          {/* En-tête */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Gestion des Coachs</h2>
              <p className="text-muted mb-0">Liste de tous les coachs inscrits</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={openAddModal}>➕ Ajouter un coach</Button>
              <Button variant="success" onClick={exportCoachesCSV}>📊 Exporter CSV</Button>
            </div>
          </div>

          {/* Filtres */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <Row className="g-3 align-items-center">
                <Col md={8}>
                  <InputGroup>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control type="text" placeholder="Rechercher par nom, prénom ou sport..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <div className="text-end">
                    <Badge bg="warning" text="dark" className="fs-6">🏅 {filteredCoaches.length} coach(s)</Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tableau */}
          <Card className="shadow-sm border-0">
            <Card.Header style={{ background: '#856404', color: 'white' }}>
              <h4 className="mb-0">🏅 Liste des Coachs</h4>
            </Card.Header>
            <Card.Body className="p-0">
              {filteredCoaches.length > 0 ? (
                <Table responsive hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Nom</th>
                      <th>Prénom</th>
                      <th>Sports enseignés</th>
                      <th>Date d'inscription</th>
                      <th className="text-center">RGPD</th>
                      <th className="text-center">QR Code</th>
                      <th className="text-center">Présences</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCoaches.map((coach) => (
                      <tr key={coach.id}>
                        <td className="fw-bold">{coach.last_name}</td>
                        <td className="fw-medium">{coach.first_name}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(coach.sports || []).map(s => <Badge key={s} bg="primary" className="px-2 py-1">{s}</Badge>)}
                            {(!coach.sports || coach.sports.length === 0) && <span className="text-muted fst-italic">Non renseigné</span>}
                          </div>
                        </td>
                        <td>
                          {new Date(coach.created_at).toLocaleDateString('fr-FR')}<br />
                          <small className="text-muted">
                            {new Date(coach.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </small>
                        </td>
                        <td className="text-center">
                          {coach.rgpd_consent ? <Badge bg="success">✓ Accepté</Badge> : <Badge bg="danger">✗ Non</Badge>}
                        </td>
                        <td className="text-center">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Voir le QR Code</Tooltip>}>
                            <Button variant="outline-warning" size="sm" onClick={() => openQRModal(coach)}>📷 QR</Button>
                          </OverlayTrigger>
                        </td>
                        <td className="text-center">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Voir le taux de présence par session</Tooltip>}>
                            <Button
                              variant="outline-purple"
                              size="sm"
                              onClick={() => openStatsModal(coach)}
                              style={{ borderColor: '#6f42c1', color: '#6f42c1', fontWeight: '600' }}
                              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#6f42c1'; e.currentTarget.style.color = '#fff' }}
                              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6f42c1' }}
                            >
                              📈 Stats
                            </Button>
                          </OverlayTrigger>
                        </td>

                        {/* ── Colonne Actions (Modifier + Supprimer) ── */}
                        <td className="text-center">
                          <div className="d-flex gap-1 justify-content-center">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Modifier ce coach</Tooltip>}>
                              <Button variant="outline-primary" size="sm" onClick={() => openEditModal(coach)}>✏️</Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer ce coach</Tooltip>}>
                              <Button variant="outline-danger" size="sm" onClick={() => openDeleteModal(coach)}>🗑️</Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-5">
                  <div style={{ fontSize: '4rem' }}>🏅</div>
                  <p className="mt-3 text-muted">{searchTerm ? 'Aucun coach trouvé' : 'Aucun coach inscrit'}</p>
                  {searchTerm
                    ? <Button variant="outline-primary" onClick={() => setSearchTerm('')}>Réinitialiser la recherche</Button>
                    : <Button variant="primary" onClick={openAddModal}>➕ Ajouter le premier coach</Button>
                  }
                </div>
              )}
            </Card.Body>
          </Card>

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* ── Modal Ajout / Modification ──────────────────────────────────── */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="lg" centered>
            <Modal.Header closeButton style={{ background: formMode === 'add' ? '#0d6efd' : '#0891b2', color: 'white' }}>
              <Modal.Title>{formMode === 'add' ? '➕ Ajouter un coach' : `✏️ Modifier — ${editingCoach?.first_name} ${editingCoach?.last_name}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {formError && <Alert variant="danger">{formError}</Alert>}
              {formSuccess && <Alert variant="success">{formSuccess}</Alert>}

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Form.Label className="fw-semibold">Prénom <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text" name="first_name" value={formData.first_name}
                    onChange={handleFormChange} placeholder="Ex : Jean"
                    isInvalid={formError && !formData.first_name.trim()}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text" name="last_name" value={formData.last_name}
                    onChange={handleFormChange} placeholder="Ex : Dupont"
                    isInvalid={formError && !formData.last_name.trim()}
                  />
                </Col>
              </Row>

              {/* Sports */}
              <Form.Label className="fw-semibold">Sports enseignés</Form.Label>

              {/* Sports sélectionnés */}
              {formData.sports.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {formData.sports.map(s => (
                    <Badge key={s} bg="primary" className="px-2 py-1 d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                      {s}
                      <span
                        style={{ cursor: 'pointer', marginLeft: 4, fontWeight: 700 }}
                        onClick={() => removeSport(s)}
                      >×</span>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Sélection rapide */}
              <div className="d-flex flex-wrap gap-1 mb-3">
                {SPORTS_DISPONIBLES.filter(s => !formData.sports.includes(s)).map(s => (
                  <Badge
                    key={s} bg="light" text="dark"
                    className="px-2 py-1"
                    style={{ cursor: 'pointer', border: '1px solid #dee2e6', fontSize: '0.85rem' }}
                    onClick={() => toggleSport(s)}
                  >
                    + {s}
                  </Badge>
                ))}
              </div>

              {/* Sport personnalisé */}
              <InputGroup className="mb-3">
                <Form.Control
                  type="text" placeholder="Ajouter un sport personnalisé..."
                  value={sportInput} onChange={e => setSportInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomSport())}
                />
                <Button variant="outline-secondary" onClick={addCustomSport}>Ajouter</Button>
              </InputGroup>

              {/* RGPD */}
              <Form.Check
                type="checkbox" name="rgpd_consent"
                id="rgpd-consent-check"
                label="Consentement RGPD accepté"
                checked={formData.rgpd_consent}
                onChange={handleFormChange}
                className="mt-2"
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowFormModal(false)} disabled={formLoading}>Annuler</Button>
              <Button
                variant={formMode === 'add' ? 'primary' : 'info'}
                onClick={handleFormSubmit}
                disabled={formLoading}
              >
                {formLoading
                  ? <><Spinner animation="border" size="sm" className="me-2" />Enregistrement...</>
                  : formMode === 'add' ? '➕ Ajouter' : '💾 Enregistrer'
                }
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* ── Modal Confirmation Suppression ──────────────────────────────── */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
            <Modal.Header closeButton style={{ background: '#dc3545', color: 'white' }}>
              <Modal.Title>🗑️ Confirmer la suppression</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Alert variant="warning" className="mb-3">
                <strong>⚠️ Attention !</strong> Cette action est irréversible.
              </Alert>
              <p>
                Êtes-vous sûr de vouloir supprimer le coach&nbsp;
                <strong>{coachToDelete?.first_name} {coachToDelete?.last_name}</strong> ?
              </p>
              <p className="text-muted small">
                Toutes les données associées à ce coach (présences, QR code…) pourront être impactées.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>Annuler</Button>
              <Button variant="danger" onClick={handleDeleteCoach} disabled={deleteLoading}>
                {deleteLoading
                  ? <><Spinner animation="border" size="sm" className="me-2" />Suppression...</>
                  : '🗑️ Supprimer définitivement'
                }
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ── Modal QR Code ─────────────────────────────────────────────────── */}
          <Modal show={showQRModal} onHide={() => setShowQRModal(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>🎫 QR Code — Coach {selectedCoach?.first_name} {selectedCoach?.last_name}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
              {selectedCoach && (
                <>
                  <div className="mb-4">
                    <h4 className="mb-1">{selectedCoach.first_name} {selectedCoach.last_name}</h4>
                    <div className="d-flex flex-wrap gap-1 justify-content-center mb-1">
                      {(selectedCoach.sports || []).map(s => <Badge key={s} bg="primary">{s}</Badge>)}
                    </div>
                    <p className="text-muted mb-0 small">Coach depuis le {new Date(selectedCoach.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div className="mb-4 d-flex justify-content-center">
                    <QRCodeSVG id="coach-qr-code-svg" value={selectedCoach.token} size={256} level="H" includeMargin
                      style={{ border: '10px solid white', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }} />
                  </div>
                  <Alert variant="info" className="mb-4">
                    <small><strong>📱 Instructions :</strong> Le coach présente ce QR code à chaque session pour signer la feuille de présence.</small>
                  </Alert>
                  <div className="d-grid gap-2 d-md-flex justify-content-center mb-4">
                    <Button variant="primary" onClick={handlePrintQRCode} size="lg">🖨️ Imprimer</Button>
                    <Button variant="success" onClick={downloadQRCodeAsPNG} size="lg">📥 Télécharger PNG</Button>
                  </div>
                  <div className="p-3 bg-light rounded text-start">
                    <small className="text-muted"><strong>Token unique :</strong><br /><code className="text-break">{selectedCoach.token}</code></small>
                  </div>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowQRModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>

          {/* ── Modal Statistiques de Présence ────────────────────────────────── */}
          <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} size="lg" centered>
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #6f42c1, #4f46e5)', color: '#fff' }}>
              <Modal.Title>📈 Présences — Coach {statsCoach?.first_name} {statsCoach?.last_name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {statsLoading && (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Chargement des statistiques...</p>
                </div>
              )}
              {!statsLoading && statsData?.error && (
                <Alert variant="danger">Impossible de charger les statistiques.</Alert>
              )}
              {!statsLoading && statsData && !statsData.error && (
                <>
                  <Row className="mb-4 g-3">
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#4f46e5' }}>{statsData.totalSessions}</div>
                        <small className="text-muted fw-semibold">Sessions totales</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#16a34a' }}>{statsData.sportStats.length}</div>
                        <small className="text-muted fw-semibold">Sports animés</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#fefce8', border: '1px solid #fde68a' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#ca8a04' }}>{statsData.firstPresence || '—'}</div>
                        <small className="text-muted fw-semibold">1ère présence</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#fdf2f8', border: '1px solid #f9a8d4' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#db2777' }}>{statsData.lastPresence || '—'}</div>
                        <small className="text-muted fw-semibold">Dernière présence</small>
                      </div>
                    </Col>
                  </Row>
                  {statsData.totalSessions === 0 ? (
                    <Alert variant="info">Ce coach n'a encore aucune présence enregistrée.</Alert>
                  ) : (
                    <>
                      <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom fw-bold">🏅 Taux de présence par sport</Card.Header>
                        <Card.Body>
                          {statsData.sportStats.map((s, i) => (
                            <div key={s.sport} className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold">{s.sport}</span>
                                <span className="text-muted small">{s.count} session{s.count > 1 ? 's' : ''} — <strong>{s.percent}%</strong></span>
                              </div>
                              <div style={{ height: '12px', borderRadius: '6px', backgroundColor: '#e9ecef', overflow: 'hidden' }}>
                                <div style={{ width: `${s.percent}%`, height: '100%', background: getSportColor(i), borderRadius: '6px', transition: 'width 0.6s ease' }} />
                              </div>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                      <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom fw-bold">📅 Jours de présence habituels</Card.Header>
                        <Card.Body>
                          <Row className="g-2">
                            {DAYS_FR.map(dayName => {
                              const found = statsData.dayStats.find(d => d.day === dayName)
                              const isActive = !!found
                              return (
                                <Col key={dayName} xs={6} sm={4} md={3}>
                                  <div className="text-center p-2 rounded" style={{
                                    background: isActive ? '#6f42c1' : '#f8f9fa',
                                    color: isActive ? '#fff' : '#adb5bd',
                                    border: `2px solid ${isActive ? '#6f42c1' : '#e9ecef'}`,
                                  }}>
                                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{isActive ? found.count : 0}</div>
                                    <small style={{ fontWeight: 600 }}>{dayName}</small>
                                    {isActive && <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{found.percent}%</div>}
                                  </div>
                                </Col>
                              )
                            })}
                          </Row>
                        </Card.Body>
                      </Card>

                      {/* ── Liste détaillée des sessions ── */}
                      <Card className="border-0 shadow-sm mt-4">
                        <Card.Header className="bg-white border-bottom fw-bold d-flex justify-content-between align-items-center">
                          <span>📋 Détail des sessions ({presencesList.length})</span>
                        </Card.Header>
                        <Card.Body className="p-0">
                          <Table responsive hover className="mb-0" size="sm">
                            <thead className="bg-light">
                              <tr>
                                <th className="ps-3">Date</th>
                                <th>Heure</th>
                                <th>Sport</th>
                                <th>Mode</th>
                                <th className="text-center pe-3">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {presencesList.map((p) => {
                                const dateStr = p.sessions?.date || p.timestamp
                                const d = new Date(p.timestamp)
                                const sport = p.sessions?.course_types?.name || '—'
                                const dayLabel = DAYS_FR[new Date(dateStr).getDay()]
                                return (
                                  <tr key={p.id}>
                                    <td className="ps-3">
                                      <span className="fw-semibold">{new Date(dateStr).toLocaleDateString('fr-FR')}</span>
                                      <br />
                                      <small className="text-muted">{dayLabel}</small>
                                    </td>
                                    <td className="text-muted small align-middle">
                                      {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                    <td className="align-middle">
                                      <Badge bg="primary" className="px-2">{sport}</Badge>
                                    </td>
                                    <td className="align-middle">
                                      {p.mode
                                        ? <Badge bg="secondary">{p.mode}</Badge>
                                        : <span className="text-muted fst-italic small">—</span>
                                      }
                                    </td>
                                    <td className="text-center pe-3 align-middle">
                                      <div className="d-flex gap-1 justify-content-center">
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Modifier cette présence</Tooltip>}>
                                          <Button
                                            variant="outline-primary" size="sm"
                                            onClick={() => openEditPresenceModal(p)}
                                          >✏️</Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer cette présence</Tooltip>}>
                                          <Button
                                            variant="outline-danger" size="sm"
                                            onClick={() => openDeletePresenceModal(p)}
                                          >🗑️</Button>
                                        </OverlayTrigger>
                                      </div>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowStatsModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>

        </Container>

          {/* ── Modal Modifier une Présence ──────────────────────────────────── */}
          <Modal show={showEditPresenceModal} onHide={() => setShowEditPresenceModal(false)} centered>
            <Modal.Header closeButton style={{ background: '#0891b2', color: 'white' }}>
              <Modal.Title>✏️ Modifier la présence</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {editPresenceError && <Alert variant="danger">{editPresenceError}</Alert>}
              <p className="text-muted small mb-3">
                Session : <strong>{editingPresence?.sessions?.course_types?.name || '—'}</strong>
              </p>
              <Form.Label className="fw-semibold">Date et heure <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="datetime-local"
                value={editPresenceDate}
                onChange={e => setEditPresenceDate(e.target.value)}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEditPresenceModal(false)} disabled={editPresenceLoading}>Annuler</Button>
              <Button variant="primary" onClick={handleEditPresenceSubmit} disabled={editPresenceLoading}>
                {editPresenceLoading
                  ? <><Spinner animation="border" size="sm" className="me-2" />Enregistrement...</>
                  : '💾 Enregistrer'
                }
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ── Modal Supprimer une Présence ─────────────────────────────────── */}
          <Modal show={showDeletePresenceModal} onHide={() => setShowDeletePresenceModal(false)} centered>
            <Modal.Header closeButton style={{ background: '#dc3545', color: 'white' }}>
              <Modal.Title>🗑️ Supprimer la présence</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Alert variant="warning"><strong>⚠️ Attention !</strong> Cette action est irréversible.</Alert>
              <p>
                Voulez-vous supprimer la présence du&nbsp;
                <strong>
                  {presenceToDelete
                    ? new Date(presenceToDelete.sessions?.date || presenceToDelete.timestamp).toLocaleDateString('fr-FR')
                    : ''}
                </strong>
                &nbsp;— <strong>{presenceToDelete?.sessions?.course_types?.name || '—'}</strong> ?
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeletePresenceModal(false)} disabled={deletePresenceLoading}>Annuler</Button>
              <Button variant="danger" onClick={handleDeletePresence} disabled={deletePresenceLoading}>
                {deletePresenceLoading
                  ? <><Spinner animation="border" size="sm" className="me-2" />Suppression...</>
                  : '🗑️ Supprimer'
                }
              </Button>
            </Modal.Footer>
          </Modal>
      </Layout>
    </ProtectedRoute>
  )
}

export default Coaches